import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Loader2, X as XIcon, Phone, MapPin, Truck, Clock } from 'lucide-react';
import { addDays, ymd } from '../porters/porterUi';
import { JobAddressLinks } from '../AddressLinks';
import { TravelLegs, DayMiles, useDayMiles } from '../travel/TravelLegs';

const START_H = 6;
const END_H = 23;
const ROW = 44;
const WASTE_BG = '#7c3aed';
const AB_BG = '#fbbf24';
const isWaste = (b) => /clearance|waste|rubbish/.test([b.service, b.van_recommendation, b.property_size].filter(Boolean).join(' ').toLowerCase());
const mins = (t) => { const [h, m] = String(t || '09:00').split(':').map(n => parseInt(n, 10)); return (h || 0) * 60 + (m || 0); };
const dur = (h) => { const m = String(h || '').match(/(\d+(?:\.\d+)?)/); return Math.max(0.5, Math.min(16, m ? parseFloat(m[1]) : 2)); };
const monday = (s) => { const d = new Date(s + 'T00:00:00'); return addDays(s, -((d.getDay() + 6) % 7)); };
const label = (s, o) => new Date(s + 'T00:00:00').toLocaleDateString('en-GB', o);
const jobStart = (b) => mins(b.booking?.time);
const jobDur = (b) => dur(b.hours_estimate) * 60;
const toPx = (m) => ((m - START_H * 60) / 60) * ROW;

const rangeFor = (range, cursor) => {
  if (range === 'day') return [cursor, 1];
  if (range === 'week') return [monday(cursor), 7];
  return [monday(cursor.slice(0, 8) + '01'), 42];
};

const bgFor = (b) => (b.completed_at ? '#10b981' : isWaste(b) ? WASTE_BG : AB_BG);

// Give overlapping jobs side-by-side lanes so every block stays visible.
const lanes = (jobs) => {
  const sorted = [...jobs].sort((a, b) => mins(a.booking?.time) - mins(b.booking?.time));
  const out = [];
  let group = [];
  let groupEnd = -1;
  const flush = () => { const n = Math.max(...group.map(g => g.lane)) + 1; group.forEach(g => out.push({ ...g, n })); group = []; };
  sorted.forEach(b => {
    const s = mins(b.booking?.time);
    const e = s + dur(b.hours_estimate) * 60;
    if (group.length && s >= groupEnd) flush();
    const used = new Set(group.filter(g => g.end > s).map(g => g.lane));
    let lane = 0;
    while (used.has(lane)) lane += 1;
    group.push({ b, lane, end: e });
    groupEnd = Math.max(groupEnd, e);
  });
  if (group.length) flush();
  return out;
};

const Block = ({ b, lane = 0, n = 1, onOpen }) => {
  const top = ((mins(b.booking?.time) - START_H * 60) / 60) * ROW;
  const bg = bgFor(b);
  return (
    <button type="button" onClick={() => onOpen(b)} className="absolute rounded-md px-1 py-0.5 text-left overflow-hidden shadow-sm border border-white hover:ring-2 hover:ring-[color:var(--mv-navy)]"
      style={{ top: Math.max(0, top), height: Math.max(22, dur(b.hours_estimate) * ROW - 2), left: `calc(${(lane / n) * 100}% + 1px)`, width: `calc(${100 / n}% - 2px)`, background: bg, color: bg === AB_BG ? '#0b2545' : '#fff' }} data-testid={`dcal-block-${b.ref}`}>
      <div className="text-[10px] font-black leading-tight">{b.booking?.time}</div>
      <div className="text-[10px] font-bold leading-tight truncate">{b.name}</div>
    </button>
  );
};

const TimeGrid = ({ dates, bookings, vans, miles, yard, onOpen }) => (
  <div className="overflow-x-auto">
    <div className="grid min-w-full" style={{ gridTemplateColumns: `36px repeat(${dates.length}, minmax(${dates.length > 1 ? 44 : 200}px, 1fr))` }}>
      <div />
      {dates.map(d => (
        <div key={d} className={`text-center pb-1 ${d === ymd(new Date()) ? 'text-[color:var(--mv-orange)]' : 'text-slate-600'}`}>
          <div className="text-[10px] font-black uppercase">{label(d, { weekday: 'short' })}</div>
          <div className="text-xs font-bold">{label(d, { day: 'numeric', month: 'short' })}</div>
          {vans[d] && <div className="text-[9px] font-bold text-slate-500 truncate" data-testid={`dcal-van-${d}`}>{vans[d].name || vans[d].reg}</div>}
          {miles[d] && <div className="mt-0.5"><DayMiles t={Object.values(miles[d])[0]} compact={dates.length > 1} testId={`dcal-day-miles-${d}`} /></div>}
        </div>
      ))}
      <div className="relative" style={{ height: (END_H - START_H + 1) * ROW }}>
        {Array.from({ length: END_H - START_H + 1 }, (_, i) => <div key={i} className="absolute right-1 text-[9px] text-slate-400" style={{ top: i * ROW - 5 }}>{String(START_H + i).padStart(2, '0')}</div>)}
      </div>
      {dates.map(d => (
        <div key={d} className="relative border-l border-slate-100" style={{ height: (END_H - START_H + 1) * ROW, backgroundImage: `repeating-linear-gradient(to bottom, #f1f5f9 0 1px, transparent 1px ${ROW}px)` }}>
          {lanes(bookings.filter(b => b.booking?.date === d)).map(({ b, lane, n }) => <Block key={b.id} b={b} lane={lane} n={n} onOpen={onOpen} />)}
          <TravelLegs jobs={bookings.filter(b => b.booking?.date === d)} startOf={jobStart} durOf={jobDur} toPx={toPx} homePc={yard} compact={dates.length > 1} />
        </div>
      ))}
    </div>
  </div>
);

const MonthGrid = ({ dates, bookings, month, onOpen, onDay }) => (
  <div className="grid grid-cols-7 gap-1">
    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(w => <div key={w} className="text-[10px] font-black uppercase text-slate-500 text-center">{w}</div>)}
    {dates.map(d => {
      const jobs = bookings.filter(b => b.booking?.date === d);
      const inMonth = d.slice(0, 7) === month;
      return (
        <div key={d} className={`min-h-[64px] rounded-md border p-1 ${inMonth ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'} ${d === ymd(new Date()) ? 'ring-2 ring-[color:var(--mv-orange)]' : ''}`} data-testid={`dcal-month-${d}`}>
          <button type="button" onClick={() => onDay(d)} className="text-[11px] font-black text-[color:var(--mv-navy)]">{parseInt(d.slice(8), 10)}</button>
          {jobs.slice(0, 3).map(b => (
            <button key={b.id} type="button" onClick={() => onOpen(b)} className="block w-full text-left text-[9px] font-bold rounded px-1 mt-0.5 truncate" style={{ background: bgFor(b), color: bgFor(b) === AB_BG ? '#0b2545' : '#fff' }} data-testid={`dcal-chip-${b.ref}`}>{b.booking?.time}<span className="hidden sm:inline"> {b.name}</span></button>
          ))}
          {jobs.length > 3 && <div className="text-[9px] text-slate-500">+{jobs.length - 3} more</div>}
        </div>
      );
    })}
  </div>
);

const JobModal = ({ b, van, onClose }) => (
  <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="dcal-modal">
    <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-xl p-5 space-y-2 text-sm">
      <div className="flex items-center gap-2">
        <h3 className="flex-1 font-display text-lg font-extrabold text-[color:var(--mv-navy)]">#{b.ref} · {b.name}</h3>
        <button type="button" onClick={onClose} className="w-10 h-10 inline-flex items-center justify-center text-slate-500" aria-label="Close" data-testid="dcal-modal-close"><XIcon className="w-5 h-5" /></button>
      </div>
      <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[color:var(--mv-orange)]" /><strong>{label(b.booking.date, { weekday: 'short', day: 'numeric', month: 'short' })} · {b.booking.time}</strong>{b.hours_estimate ? ` · ${b.hours_estimate}` : ''}</div>
      <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-slate-500 shrink-0 mt-1.5" /><JobAddressLinks job={b} testid={`dcal-addresses-${b.ref}`} /></div>
      <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-slate-500" />{van ? `${van.name || ''} ${van.reg}` : 'Van not set'} · {b.van_recommendation || b.service}</div>
      {b.phone && <a href={`tel:${b.phone}`} className="flex items-center gap-2 font-bold text-[color:var(--mv-orange)]"><Phone className="w-4 h-4" />{b.phone}</a>}
      {b.notes && <div className="text-xs bg-slate-50 rounded p-2 text-slate-600">{b.notes}</div>}
      {b.completed_at && <div className="text-xs font-black uppercase text-emerald-700">Done</div>}
      {document.getElementById(`job-${b.id}`) && (
        <button type="button" onClick={() => { onClose(); document.getElementById(`job-${b.id}`).scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="w-full h-11 rounded-md bg-[color:var(--mv-navy)] text-white font-bold" data-testid="dcal-open-card">Open job card</button>
      )}
    </div>
  </div>
);

export const DriverCalendar = ({ authFetch, refreshKey }) => {
  const [range, setRange] = useState('week');
  const [cursor, setCursor] = useState(ymd(new Date()));
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [start, days] = rangeFor(range, cursor);
  const { totals: miles, yard } = useDayMiles(start, days, `${refreshKey}`);
  useEffect(() => {
    let live = true;
    authFetch('get', `/driver/calendar?start=${start}&days=${days}`).then(({ data }) => { if (live) setData(data); }).catch(() => {});
    return () => { live = false; };
  }, [authFetch, start, days, refreshKey]);
  const step = (dir) => {
    if (range === 'month') { const d = new Date(cursor.slice(0, 8) + '01T00:00:00'); d.setMonth(d.getMonth() + dir); setCursor(ymd(d)); }
    else setCursor(addDays(cursor, dir * days));
  };
  const title = range === 'month' ? label(cursor, { month: 'long', year: 'numeric' })
    : range === 'week' ? `${label(start, { day: 'numeric', month: 'short' })} – ${label(addDays(start, 6), { day: 'numeric', month: 'short' })}`
      : label(cursor, { weekday: 'long', day: 'numeric', month: 'short' });
  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-4" data-testid="driver-calendar">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)] flex items-center gap-2 flex-1"><CalIcon className="w-5 h-5 text-[color:var(--mv-orange)]" />My calendar</h2>
        <div className="inline-flex rounded-md border border-slate-200 p-0.5" data-testid="dcal-range">
          {['day', 'week', 'month'].map(r => <button key={r} type="button" onClick={() => setRange(r)} className={`px-3 h-8 rounded text-xs font-bold capitalize ${range === r ? 'bg-[color:var(--mv-navy)] text-white' : 'text-slate-600'}`} data-testid={`dcal-range-${r}`}>{r}</button>)}
        </div>
      </div>
      <div className="flex items-center gap-1 mb-3">
        <button type="button" onClick={() => step(-1)} className="h-9 w-9 rounded-md border border-slate-200 inline-flex items-center justify-center" aria-label="Previous" data-testid="dcal-prev"><ChevronLeft className="w-4 h-4" /></button>
        <button type="button" onClick={() => setCursor(ymd(new Date()))} className="h-9 px-3 rounded-md border border-slate-200 text-xs font-bold" data-testid="dcal-today">Today</button>
        <button type="button" onClick={() => step(1)} className="h-9 w-9 rounded-md border border-slate-200 inline-flex items-center justify-center" aria-label="Next" data-testid="dcal-next"><ChevronRight className="w-4 h-4" /></button>
        <div className="ml-2 text-sm font-black text-[color:var(--mv-navy)]" data-testid="dcal-title">{title}</div>
        {data && <div className="ml-auto text-xs text-slate-500" data-testid="dcal-count">{data.bookings.length} job{data.bookings.length === 1 ? '' : 's'}</div>}
      </div>
      {!data ? <Loader2 className="w-5 h-5 animate-spin" /> : range === 'month'
        ? <MonthGrid dates={data.dates} bookings={data.bookings} month={cursor.slice(0, 7)} onOpen={setSel} onDay={(d) => { setCursor(d); setRange('day'); }} />
        : <TimeGrid dates={data.dates} bookings={data.bookings} vans={data.vans} miles={miles} yard={yard} onOpen={setSel} />}
      {sel && <JobModal b={sel} van={data?.vans?.[sel.booking?.date]} onClose={() => setSel(null)} />}
    </section>
  );
};
